<template>
  <div
    class="fixed"
    :class="{ debug: debug }"
    style="background: black"
  >
    <warning-message
      v-if="!screen.loaded"
      is-loading-message
    >
      {{ $t('status.loading') }}
    </warning-message>
    <warning-message
      v-else-if="todaysNowPlayingAndUpcomingShowings.length == 0"
    >
      {{ $t('showing.noShowingsToday') }}
    </warning-message>

    <div v-else-if="screen && currentShowing">
      <!-- We have a movie and showtimes-->
      <div
        class="row column jusitfy-center items-center absolute-full poster-and-showings-container"
        :style="{
          top: marginVertical + 'px ',
          bottom: marginVertical + 'px ',
          left: +marginHorizontal + 'px',
          right: +marginHorizontal + 'px',
        }"
      >
        <div
          class="row column justify-between full-height full-width text-white"
        >
          <div
            v-if="showLogo"
            class="col-auto row relative-position justify-center items-center"
            style="padding-top: 1vh;"
          >
            <img
              style="height: 4vh"
              :src="currentSite.lightLogoImageUrl"
            >
          </div>
          <div
            class="col-auto relative-position"
            style="margin-top: 4vh;"
          >
            <span
              v-if="showingStatus"
              class="absolute-bottom text-bold z-top q-mx-auto text-uppercase rounded-borders"
              :class="$isColorDark(showingStatusColor) ? 'text-white' : 'text-dark'"
              style="padding: 0.1vh 0.5vh; max-width: fit-content;"
              :style="{
                background: showingStatusColor,
                'font-size': `${showingStatusFontSize}vw`,
                'margin-bottom': `-${showingStatusFontSize}vw`
              }"
            >
              {{ showingStatus }}
            </span>
          </div>
          <div class="col column justify-between">
            <video
              v-if="currentShowing.movie.animatedPosterVideo && showMotionPoster"
              :key="currentShowing.movie.id"
              autoplay
              loop
              muted
              style="width: 100%; height: 100%; min-height: 50vh; max-height: 73vh; object-fit: cover;"
              :style="posterStyle"
              class="col"
            >
              <source
                :src="currentShowing.movie.animatedPosterVideoUrl"
                type="video/mp4"
              >
            </video>
            <q-img
              v-else
              class="col"
              :style="posterStyle"
              :ratio="2/3"
              :src="currentShowing.movie.posterImageLargeUrl"
            />
            <div
              v-if="showMovieName"
              class="col-auto text-center text-white text-bold ellipsis-3-lines"
              style="line-height: 1.1; margin: 0.8vh 1vh;"
              :style="fontSize"
            >
              {{ currentShowing.movie.name }}
            </div>
            <div
              class="col-auto row justify-center q-mx-md"
              style="padding: 0.5vh 0; margin: 0.5vh 1vh"
            >
              <div class="col-auto row justify-center items-center">
                <template v-if="currentSite.signageConfig.display_rating">
                  <rated
                    v-if="currentShowing.movie.rating"
                    :rated="currentShowing.movie.rating"
                    :rating-reason="currentShowing.movie.ratingReason"
                    light-text
                    is-font-size-responsive
                    :font-size="2.2"
                    :no-margin="false"
                    :highlight-r-rated="highlightRRated"
                    :disable-official-symbol="disableOfficialSymbol"
                    class="text-bold q-mr-md"
                  />
                  <q-chip
                    v-if="showRatingReason && currentShowing.movie.ratingReason"
                    class="text-bold"
                    square
                    color="dark"
                    text-color="white"
                    size="2vh"
                    dense
                  >
                    <div
                      class="q-py-sm q-px-none"
                      style="white-space: normal; font-size: 1.2vh; line-height: 1;"
                    >
                      {{ currentShowing.movie.ratingReason }}
                    </div>
                  </q-chip>
                </template>
                <template
                  v-if="showShowingBadges"
                >
                  <showing-badge
                    v-for="showingBadge in currentShowing.showingBadges.data"
                    :key="showingBadge.id"
                    class="col-auto"
                    size="3vw"
                    :showing-badge="showingBadge"
                    color="white"
                  />
                </template>
              </div>
            </div>
          </div>
          <div

            class="col-auto row relative-position items-center"
            :class="showScreenName ? 'justify-between' : 'justify-center'"
          >
            <div
              v-if="showScreenName"
              class="col-auto text-bold text-uppercase text-white"
              style="font-size: 4vh;"
            >
              {{ screen.name }}
            </div>
            <div
              class="text-bold text-uppercase col-auto text-white"
              style="font-size: 4vh;"
            >
              {{ currentShowing.siteTime.format('h:mm a') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <warning-message
      v-else-if="errorOccurred"
    >
      An error occurred
    </warning-message>
    <warning-message
      v-else-if="!screen"
    >
      {{ `Unknown Screen - ${$route.params && $route.params.screenId}` }}
    </warning-message>
    <warning-message
      v-else
    >
      Unknown Error
    </warning-message>
  </div>
</template>

<script>
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'
import Rated from 'src/modules/consumer/components/Rated'
import WarningMessage from 'src/modules/signage/components/WarningMessage'
import CurrentShowingsSignageMixin from 'src/modules/signage/mixins/CurrentShowingsSignageMixin'
import ShowingsSignageConfigurationMixin from 'src/modules/signage/mixins/ShowingsSignageConfigurationMixin'
import { inject } from 'vue'

export default {
  name: 'ScreenPortraitStatusStream',
  components: {
    ShowingBadge,
    Rated,
    WarningMessage
  },
  mixins: [CurrentShowingsSignageMixin, ShowingsSignageConfigurationMixin],
  props: {
    overrideScreenId: {
      type: Number,
      required: false,
      default: null
    },
    overrideCover: {
      type: Boolean,
      required: false,
      default: false
    },
    showScreenName: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return !(route?.query && route.query['show-screen-name'] === 'false')
      }
    },
    showMovieName: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return !(route?.query && route.query['show-movie-name'] === 'false')
      }
    },
    showShowingBadges: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return !(route?.query && route.query['show-showing-badges'] === 'false')
      }
    },
    showRatingReason: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return !(route?.query && route.query['show-rating-reason'] === 'false')
      }
    },
    posterShowBorder: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['poster-show-border'] === 'true'
      }
    },
    showMotionPoster: {
      type: Boolean,
      required: false,
      default () {
        const route = inject('route')
        return route?.query && route.query['show-motion-poster'] === 'true'
      }
    }
  },
  models: {
    screen: {
      fields: ['id', 'name', 'color'],
      todaysShowings: {
        movie: 'default',
        screen: 'default',
        showingBadges: 'default',
        withinSignageThreshold: true
      },
      variables () {
        return {
          id: this.overrideScreenId || this.$route.params?.screenId
        }
      },
      immediate: true
    }
  },
  data () {
    return {
      errorOccurred: false
    }
  },
  computed: {
    posterBorderRadius () {
      return this.$route.query['poster-border-radius'] || 2
    },
    showingStatusFontSize () {
      return this.$route.query['showing-status-font-size'] || 4
    },
    cover () {
      return this.overrideCover || this.$route.query.cover || false
    },
    fontSize () {
      const movieNameLetterCount = this.currentShowing.movie.name.length

      if (movieNameLetterCount > 45) {
        return 'font-size : 2.6vh'
      } else if (movieNameLetterCount > 18) {
        return 'font-size : 3.4vh'
      } else {
        return 'font-size : 4vh'
      }
    },
    posterStyle () {
      const style = []
      if (this.posterShowBorder) {
        style.push(`border: 0.4vh solid ${this.showingStatusColor}`)
      }

      if (this.posterBorderRadius) {
        style.push(`border-radius: ${this.posterBorderRadius}vh`)
      }

      return style.join(';')
    }
  },
  async mounted () {
    // Fetching the screen when in the app mode of digital-signage, sets the site_id cookie
    // This will reload the configuration data after being authenticated
    this.$handleAuthChange()
  }
}
</script>

<style scoped>
.fixed {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
.debug .poster-and-showings-container > div {
  border: solid 1px red;
}

.showing {
  font-size: 1.2vh;
  padding: 0 1vw;
  overflow: hidden;
  margin-left: 0.5vh;
  margin-bottom: 0.5vw;
}
.before-after {
  font-size: 1vw;
  font-weight: bold;
  margin-bottom: 1vw;
}
.debug .showing {
  border: solid 1px yellow;
}
.time {
  font-size: 2vh;
  font-weight: 800;
  line-height: 1;
}
</style>
